import { motion } from 'framer-motion';
import styled, { css, keyframes } from 'styled-components';

import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { ReactComponent as CloseIcon } from '@pkg/components/Sections/Profile/icons/close.svg';
import { ReactComponent as PlayButtonIcon } from '@pkg/components/Sections/Profile/icons/play-button.svg';

import { Draggable } from './components';

interface NftAssetProps {
    $hasBackground?: boolean;
}

export const NftAssetWrapper = styled.div<NftAssetProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1 / 1;
    border-radius: 10px;
    position: relative;
    width: 100%;
    transition: background ${({ theme }) => theme.transitions.speeds.normal}s;

    ${({ $hasBackground, theme }) =>
        $hasBackground &&
        css`
            background: ${theme.shades.white[5]};
        `}
`;

const FADE_IN = keyframes`
    to {
        opacity: 1;
    }
`;

interface StyledDraggableProps {
    $hidden?: boolean;
    $loaded?: boolean;
    $hasSquareAspectRatio: boolean;
    $disabled: boolean;
}

export const StyledDraggable = styled(Draggable)<StyledDraggableProps>`
    opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
    transition: ${({ theme }) => theme.transitions.speeds.fast}s;

    ${({ $hasSquareAspectRatio }) =>
        !$hasSquareAspectRatio &&
        css`
            padding: 10px;
        `}

    img,
    video {
        opacity: 0;
        border-radius: 10px !important;
        max-height: 100%;
        user-drag: none;

        ${({ $loaded }) =>
            $loaded &&
            css`
                animation: ${FADE_IN} ${({ theme }) => theme.transitions.speeds.normal}s forwards;
            `}
    }

    ${({ $disabled }) =>
        $disabled &&
        css`
            pointer-events: none;
            opacity: 0.3;
        `}
`;

export const PlayButtonWrapper = styled(motion.div)`
    position: absolute;
    bottom: 10px;
    right: 10px;
    padding: 9px;
    border-radius: 50%;
    background: ${({ theme }) => theme.shades.white[10]};
    transition: background ${({ theme }) => theme.transitions.speeds.rapid}s;

    &:hover {
        background: ${({ theme }) => theme.shades.white[40]};
    }
`;

export const StyledPlayButton = styled(PlayButtonIcon)`
    margin-left: 2px;
`;

export const QuickAddWrapper = styled(motion.div)`
    position: absolute;
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
`;

export const AddButton = styled(Button)`
    &&& {
        white-space: nowrap;

        @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
            display: none;
        }
    }
`;

export const DisabledMessage = styled.div`
    border-radius: 10px;
    background-color: ${({ theme }) => theme.shades.white[20]};
    text-transform: uppercase;
    padding: 8px;
    width: fit-content;

    position: absolute;
    left: 10px;
    bottom: 10px;
`;

export const ErrorWrapper = styled.div`
    &&& {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        user-select: none;
        pointer-events: none;
    }
`;
export const ErrorIconWrapper = styled.div`
    &&& {
        width: 16px;
        height: 16px;
        background: ${({ theme }) => theme.colors.white};
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;
export const ErrorIcon = styled(CloseIcon)`
    &&& {
        margin: 0 auto;
        width: 6px;
        height: 6px;
        path {
            fill: #090719;
        }
    }
`;
export const ErrorText = styled.span`
    &&& {
        font-family: 'Alliance No.1';
        font-style: normal;
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
        text-align: center;
        letter-spacing: -0.1px;
        color: ${({ theme }) => theme.colors.white};
        opacity: 0.5;
    }
`;

export const ErrorSquare = styled.div`
    &&& {
        width: 40px;
        height: 40px;
        background: ${({ theme }) => theme.shades.white[20]};
        backdrop-filter: blur(12px);
        border-radius: 8px;
        margin-bottom: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

export const NotSupportedOverlay = styled.div`
    position: absolute;
    top: 0%;
    left: 0%;
    width: 100%;
    height: 100%;
`;

export const NotSupportedPill = styled.div`
    position: absolute;
    top: 6px;
    left: 5px;
    border-radius: 6px;
    height: 20px;
    display: flex;
    align-items: center;
    background: ${({ theme }) => theme.shades.white[10]};
    padding: 2px 5px;
`;

export const NotSupportedText = styled.p`
    font-family: 'Alliance No.1';
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
    letter-spacing: -0.1px;
    margin-left: 5px;
    margin-right: 1px;
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.white};
`;
